import { useTranslation } from 'react-i18next';

import { TAG_TYPES } from '../../../../../Shared/constants/commonConstants';
import {
  SOV_CONTEXTUAL_NODE_FIELDS,
  SOV_CONTEXTUAL_NODE_KEYS,
  TYPE_OF_PURCHASE,
} from '../../constants/DetailedBookingConstants';
import {
  DetailedBookingSOVData,
  PreviousSOVValues,
  SlotOptionType,
  SlotPackage,
  SovActiveModalType,
  SOVContextualNodeData,
  SOVContextualNodeErrors,
} from '../../interface/DetailedBookingInterface';
import { Option } from '../../interface/FormFieldInterfaces';
import { handleSOVContextualNodeChange } from '../../services/sovFormHelpers/handleContextualNodeChange';
import {
  FieldContainer,
  FieldInfoText,
  FieldLabel,
} from '../CreateBookingForm/CreateDetailedBookingForm.styles';
import { FormFieldWrapper } from '../FormFieldWrapper';

export interface PartialContextualFieldProps {
  contextualNodes: SOVContextualNodeData[];
  formData: DetailedBookingSOVData;
  isContextualSelected: boolean;
  slotPackagesData: SlotPackage[];
  hasValidationBeenTriggered: boolean;
  contextualNodeErrors: SOVContextualNodeErrors[];
  setContextualNodes: React.Dispatch<
    React.SetStateAction<SOVContextualNodeData[]>
  >;
  setFormData: React.Dispatch<React.SetStateAction<DetailedBookingSOVData>>;
  setActiveModal: SovActiveModalType;
  roValue: number;
  selectedOption?: SlotOptionType;
  setContextualPercentageAvailable: React.Dispatch<
    React.SetStateAction<number>
  >;
  setCurrentValidationNodeId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  setPreviousValues: React.Dispatch<
    React.SetStateAction<PreviousSOVValues | null>
  >;
  isEditMode: boolean;
  isApprovedBooking?: boolean;
  existingContextualNodePercentages?: Map<string, number>;
  isWaitlistEnabled?: boolean;
}

interface ContextualNodeFieldProps extends PartialContextualFieldProps {
  node: SOVContextualNodeData;
  fieldConfig: (typeof SOV_CONTEXTUAL_NODE_FIELDS)[number];
}

const ContextualNodeField = ({
  node,
  fieldConfig,
  contextualNodes,
  formData,
  isContextualSelected,
  slotPackagesData,
  hasValidationBeenTriggered,
  contextualNodeErrors,
  setContextualNodes,
  setFormData,
  setActiveModal,
  roValue,
  selectedOption,
  setContextualPercentageAvailable,
  setCurrentValidationNodeId,
  setPreviousValues,
  isEditMode,
  isApprovedBooking,
  existingContextualNodePercentages,
  isWaitlistEnabled,
}: ContextualNodeFieldProps) => {
  const { t } = useTranslation();

  const nodeIndex = contextualNodes.findIndex((n) => n.id === node.id);
  const nodeErrors = contextualNodeErrors[nodeIndex] || {};
  const fieldError = hasValidationBeenTriggered
    ? nodeErrors[fieldConfig.key as keyof typeof nodeErrors]
    : '';
  const isFieldConfigContextual =
    fieldConfig.key === SOV_CONTEXTUAL_NODE_KEYS.CONTEXTUAL_NODE;

  let contextualNodeOptions: Option[] = [];
  if (isFieldConfigContextual) {
    if (formData.gpSlot && isContextualSelected) {
      const selectedPackageSegments = slotPackagesData.find(
        (pkg) => pkg.slotPackageName === formData.gpSlot
      )?.segments;
      if (selectedPackageSegments) {
        // keeping unused segments and current selection for segments
        contextualNodeOptions = selectedPackageSegments
          .filter(({ segmentName }) => {
            const isUsedByOtherNode = contextualNodes.some(
              ({ contextualNode, id }) =>
                contextualNode === segmentName && id !== node.id
            );
            const isCurrentSelection = node.contextualNode === segmentName;
            return !isUsedByOtherNode || isCurrentSelection;
          })
          .map(({ segmentName }) => ({
            id: segmentName,
            value: segmentName,
            label: segmentName,
          }));
      }
    } else if ('options' in fieldConfig)
      contextualNodeOptions = fieldConfig.options || [];
  }

  const shouldDisableField =
    formData.typeOfPurchase === TYPE_OF_PURCHASE.GP_SOV &&
    isContextualSelected &&
    (fieldConfig.key === SOV_CONTEXTUAL_NODE_KEYS.IMPRESSION_BOOKED ||
      fieldConfig.key === SOV_CONTEXTUAL_NODE_KEYS.VALUE_BOOKED);

  const shouldShowContextualAvailableInfo =
    isContextualSelected && isFieldConfigContextual;

  const selectedContextualNodeData = shouldShowContextualAvailableInfo
    ? slotPackagesData
        .find((pkg) => pkg.slotPackageName === formData.gpSlot)
        ?.segments.find(
          ({ segmentName }) => segmentName === node.contextualNode
        )
    : null;

  const getFieldValue = () => {
    const rawValue = node[fieldConfig.key as keyof SOVContextualNodeData];
    if (
      fieldConfig.key ===
        SOV_CONTEXTUAL_NODE_KEYS.SOV_PERCENTAGE_FOR_CONTEXTUAL_GP &&
      !rawValue
    )
      return '';
    return rawValue;
  };

  return (
    <FieldContainer key={fieldConfig.key}>
      <FieldLabel>{t(fieldConfig.labelKey)}</FieldLabel>
      <FormFieldWrapper
        id={`${fieldConfig.key}-${fieldConfig.type}-${node.id}`}
        type={fieldConfig.type}
        value={getFieldValue()}
        onChange={(value: string) =>
          handleSOVContextualNodeChange({
            nodeId: node.id,
            field: fieldConfig.key,
            value,
            setContextualNodes,
            formData,
            isContextualSelected,
            slotPackagesData,
            setFormData,
            setActiveModal,
            roValue,
            contextualNodes,
            setPreviousValues,
            isEditMode,
            selectedOption,
            setContextualPercentageAvailable,
            setCurrentValidationNodeId,
            isApprovedBooking,
            existingContextualNodePercentages,
            isWaitlistEnabled,
          })
        }
        placeholder={fieldConfig.placeholderKey}
        options={
          fieldConfig.type === TAG_TYPES.DROPDOWN_TAG_TYPE
            ? contextualNodeOptions
            : []
        }
        disabled={shouldDisableField}
        error={fieldError}
      />
      {!!selectedContextualNodeData && (
        <FieldInfoText>
          (
          {t('available-slot-percentage-info', {
            availablePercentage: selectedContextualNodeData.percentageAvailable,
          })}
          )
        </FieldInfoText>
      )}
    </FieldContainer>
  );
};

export default ContextualNodeField;
